import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AuthForm } from "../components/AuthForm";

const COPY = {
  login: {
    heading: "Welcome back",
    sub: "Sign in to continue your search.",
    submit: "Sign in",
    togglePrompt: "New to Evervow?",
    toggleAction: "Create an account",
  },
  register: {
    heading: "Begin with intention",
    sub: "Create your account. It takes two minutes.",
    submit: "Create account",
    togglePrompt: "Already have an account?",
    toggleAction: "Sign in",
  },
};

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginSuccess } = useAuth();
  const navigate = useNavigate();
  const copy = COPY[mode];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = mode === "register" ? form : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      loginSuccess(data);
      navigate(data.user.onboarded ? "/discover" : "/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setError("");
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.pexels.com/photos/5760477/pexels-photo-5760477.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940"
          alt="Couple at sunset"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1A1A1A]/20" />
        <div className="absolute bottom-12 left-12 text-[#FAF9F6]">
          <p className="font-serif text-3xl max-w-sm leading-tight">
            "We both knew what we wanted. That changed everything."
          </p>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-12 py-24"
      >
        <div className="w-full max-w-md">
          <Link to="/" className="font-serif text-2xl" data-testid="auth-brand-link">
            Evervow
          </Link>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tighter mt-12">
            {copy.heading}
          </h1>
          <p className="mt-4 text-[#5C5C5C]">{copy.sub}</p>
          <AuthForm
            mode={mode}
            form={form}
            setForm={setForm}
            error={error}
            loading={loading}
            submitLabel={loading ? "One moment…" : copy.submit}
            onSubmit={submit}
          />
          <p className="mt-8 text-sm text-[#5C5C5C]">
            {copy.togglePrompt}{" "}
            <button
              data-testid="auth-toggle-mode-button"
              onClick={toggleMode}
              className="text-[#7A3B3F] underline underline-offset-4"
            >
              {copy.toggleAction}
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
